/**
 * Sub-flow orchestration helpers for the unified canvas (ADR-0008 PR3b3).
 *
 * The WorkflowEditor renders the main workflow PLUS every linked
 * sub-flow as a "region" on the same React Flow canvas. Each region
 * is a group node; the sub-flow's own nodes/edges sit inside it as
 * children with ``parentId: <groupId>`` + ``extent: "parent"``.
 *
 * These helpers are pure (no React, no React Flow) so they can be
 * unit-tested cheaply and the editor's giant `EditorInner` doesn't
 * have to grow even more.
 *
 * Naming convention for id prefixing:
 *   - Sub-flow group node id: ``subflow:<workflow_id>``
 *   - Sub-flow child node id: ``sf:<workflow_id>:<original_node_id>``
 *   - Sub-flow child edge id: ``sf:<workflow_id>:<original_edge_id>``
 *
 * The prefix lets the editor's onChange handlers + save-time
 * extraction tell which definition a flow node belongs to, without
 * having to keep a parallel mapping table.
 */
import type {
  WorkflowDefinition,
  WorkflowNode as TrayNode,
  WorkflowEdge as TrayEdge,
} from "@/lib/api-workflows";


export interface SinkBranchLink {
  /** id of the sink node in the parent's definition */
  sinkNodeId: string;
  /** branch id (matches SinkBranch.id, e.g. "sblocca" / "succeeded") */
  branchId: string;
  /** workflow_id of the linked sub-flow (null = unlinked, hook point only) */
  linkedWorkflowId: string | null;
  /** Persisted canvas position of the sub-flow region on the PARENT
   *  canvas (SinkBranch.region_position). Null = never dragged → the
   *  editor falls back to a computed default. */
  regionPosition: { x: number; y: number } | null;
}


/** Pull every sink branch link out of a WorkflowDefinition.
 *
 *  Skips branches with no link. Implicit branches (succeeded /
 *  dead_letter) only appear here if the operator has wired them
 *  explicitly via the BranchesEditor (the catalog adds the handles
 *  automatically but doesn't add them to ``config.branches``).
 */
export function extractSinkBranchLinks(
  def: WorkflowDefinition,
): SinkBranchLink[] {
  const out: SinkBranchLink[] = [];
  for (const n of def.nodes ?? []) {
    if (n.kind !== "http_sink" && n.kind !== "connector_sink") continue;
    const cfg = (n.config ?? {}) as Record<string, unknown>;
    const branches = (cfg.branches as Array<Record<string, unknown>>) ?? [];
    for (const b of branches) {
      const sinkNodeId = n.id;
      const branchId = b?.id as string | undefined;
      const linked = (b?.linked_workflow_id as string | undefined) ?? null;
      if (!branchId) continue;
      const rawPos = b?.region_position as
        | { x?: unknown; y?: unknown }
        | null
        | undefined;
      const regionPosition =
        rawPos && typeof rawPos.x === "number" && typeof rawPos.y === "number"
          ? { x: rawPos.x, y: rawPos.y }
          : null;
      out.push({ sinkNodeId, branchId, linkedWorkflowId: linked, regionPosition });
    }
  }
  return out;
}


/** All workflow ids the editor must load alongside the main one. */
export function subflowIdsLinkedFromDefinition(
  def: WorkflowDefinition,
): string[] {
  const out = new Set<string>();
  for (const link of extractSinkBranchLinks(def)) {
    if (link.linkedWorkflowId) out.add(link.linkedWorkflowId);
  }
  return [...out];
}


// ---------------------------------------------------------------------------
// Id prefixing — keeps main + sub-flow nodes addressable in the same
// React Flow array without collisions, AND lets save-time extraction
// route each node back to its source definition.
// ---------------------------------------------------------------------------

const SUBFLOW_GROUP_PREFIX = "subflow:";
const SUBFLOW_CHILD_PREFIX = "sf:";

export function subflowGroupNodeId(workflowId: string): string {
  return `${SUBFLOW_GROUP_PREFIX}${workflowId}`;
}

export function subflowChildNodeId(
  workflowId: string,
  childId: string,
): string {
  return `${SUBFLOW_CHILD_PREFIX}${workflowId}:${childId}`;
}

export function subflowChildEdgeId(
  workflowId: string,
  edgeId: string,
): string {
  return `${SUBFLOW_CHILD_PREFIX}${workflowId}:${edgeId}`;
}

/** Reverse: given a prefixed node id, return {workflowId, originalId}
 *  or null if it's a main-flow id. */
export function parseSubflowChildId(
  prefixedId: string,
): { workflowId: string; originalId: string } | null {
  if (!prefixedId.startsWith(SUBFLOW_CHILD_PREFIX)) return null;
  const rest = prefixedId.slice(SUBFLOW_CHILD_PREFIX.length);
  const colonIdx = rest.indexOf(":");
  if (colonIdx < 0) return null;
  return {
    workflowId: rest.slice(0, colonIdx),
    originalId: rest.slice(colonIdx + 1),
  };
}

/** True when this React Flow node id is the wrapper group of a sub-flow. */
export function isSubflowGroupId(id: string): boolean {
  return id.startsWith(SUBFLOW_GROUP_PREFIX);
}

export function workflowIdFromGroupId(groupId: string): string | null {
  if (!isSubflowGroupId(groupId)) return null;
  return groupId.slice(SUBFLOW_GROUP_PREFIX.length);
}


// ---------------------------------------------------------------------------
// Splatting a sub-flow definition into the canvas
// ---------------------------------------------------------------------------

export interface CanvasSubflowSlice {
  /** The group wrapper (parent of all child nodes). */
  group: {
    id: string;
    /** absolute canvas position of the top-left corner */
    position: { x: number; y: number };
    /** rendered size — operator can resize via the group header */
    width: number;
    height: number;
    /** workflow id for the routing helpers */
    workflowId: string;
    /** parent context: which sink branch owns this sub-flow region */
    parentSinkNodeId: string | null;
    parentBranchId: string | null;
    label: string;
  };
  /** Children with prefixed ids, positions relative to the group. */
  nodes: Array<TrayNode & { _parentGroupId: string }>;
  /** Edges with prefixed ids tying prefixed child node ids. */
  edges: TrayEdge[];
}


/** Default region size — large enough to fit a couple of nodes
 *  without scrolling. The operator can drag the group to resize it
 *  (React Flow's group node supports resize handles when configured). */
export const SUBFLOW_REGION_DEFAULT_WIDTH = 480;
export const SUBFLOW_REGION_DEFAULT_HEIGHT = 280;


/** Build the canvas representation of a sub-flow's nodes/edges.
 *
 *  ``defaultPosition`` is where the group wrapper sits on the canvas;
 *  operator-set positions override it (we don't persist them in this
 *  PR — they're session-local per the design decision).
 *
 *  Child node positions in the input def are interpreted as RELATIVE
 *  to the group (which is how React Flow's parentId/extent="parent"
 *  works). For freshly-created sub-flows we expect (0,0) on the
 *  delivery_trigger; existing ones keep whatever positions they had.
 */
export function buildSubflowCanvasSlice(
  workflowId: string,
  def: WorkflowDefinition,
  opts: {
    defaultPosition: { x: number; y: number };
    label: string;
    parentSinkNodeId: string | null;
    parentBranchId: string | null;
  },
): CanvasSubflowSlice {
  const groupId = subflowGroupNodeId(workflowId);
  const prefixedNodes = (def.nodes ?? []).map((n) => ({
    ...n,
    id: subflowChildNodeId(workflowId, n.id),
    _parentGroupId: groupId,
  }));
  const prefixedEdges = (def.edges ?? []).map((e) => ({
    ...e,
    id: subflowChildEdgeId(workflowId, e.id),
    source: subflowChildNodeId(workflowId, e.source),
    target: subflowChildNodeId(workflowId, e.target),
  }));
  return {
    group: {
      id: groupId,
      position: opts.defaultPosition,
      width: SUBFLOW_REGION_DEFAULT_WIDTH,
      height: SUBFLOW_REGION_DEFAULT_HEIGHT,
      workflowId,
      parentSinkNodeId: opts.parentSinkNodeId,
      parentBranchId: opts.parentBranchId,
      label: opts.label,
    },
    nodes: prefixedNodes,
    edges: prefixedEdges,
  };
}


/** Generic shapes for the canvas extractor. Structural typing keeps
 *  this module ReactFlow-independent (tests don't need to import RF). */
export interface CanvasFlowNodeLike {
  id: string;
  parentNode?: string | undefined;
  position: { x: number; y: number };
  data: {
    kind: string;
    label?: string | null;
    config?: Record<string, unknown>;
  };
}
export interface CanvasFlowEdgeLike {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null | undefined;
}


/** Inverse of ``buildSubflowCanvasSlice``: given the live canvas
 *  ReactFlow nodes + edges (with prefixed ids and parentNode wiring),
 *  pull out the sub-flow's own nodes/edges back into a clean
 *  WorkflowDefinition shape with un-prefixed ids and positions
 *  RELATIVE to the parent region (which is what React Flow stores when
 *  ``extent: "parent"`` is set).
 *
 *  Used at save time to reconstruct each linked sub-flow's definition
 *  before POSTing the new version (N-PATCH save orchestration, PR3b3c).
 *
 *  Virtual link edges (id-prefixed ``sf-link:``) are excluded — they
 *  carry no WorkflowEdge on either side (the link lives in
 *  SinkBranch.linked_workflow_id).
 */
export function extractSubflowSliceFromCanvas(
  workflowId: string,
  canvasNodes: CanvasFlowNodeLike[],
  canvasEdges: CanvasFlowEdgeLike[],
): { nodes: TrayNode[]; edges: TrayEdge[] } {
  const groupId = subflowGroupNodeId(workflowId);
  const childPrefix = `${SUBFLOW_CHILD_PREFIX}${workflowId}:`;
  const nodes: TrayNode[] = canvasNodes
    .filter((n) => n.parentNode === groupId || n.id.startsWith(childPrefix))
    .map((n) => {
      const original = n.id.startsWith(childPrefix)
        ? n.id.slice(childPrefix.length)
        : n.id;
      return {
        id: original,
        kind: n.data.kind as TrayNode["kind"],
        label: n.data.label ?? null,
        config: n.data.config ?? {},
        position: { x: n.position.x, y: n.position.y },
      };
    });
  const edges: TrayEdge[] = canvasEdges
    .filter(
      (e) =>
        e.id.startsWith(childPrefix) &&
        !e.id.startsWith(SUBFLOW_LINK_EDGE_PREFIX),
    )
    .map((e) => ({
      id: e.id.slice(childPrefix.length),
      source: e.source.startsWith(childPrefix)
        ? e.source.slice(childPrefix.length)
        : e.source,
      target: e.target.startsWith(childPrefix)
        ? e.target.slice(childPrefix.length)
        : e.target,
      source_handle: e.sourceHandle ?? null,
    }));
  return { nodes, edges };
}


/** True when the node is a child of any sub-flow region. */
export function isSubflowChildNode<T extends { id: string }>(n: T): boolean {
  return n.id.startsWith(SUBFLOW_CHILD_PREFIX);
}


/** Prefix used for the canvas-only virtual edge linking a sink branch
 *  handle to its sub-flow region's delivery_trigger node. These edges
 *  have no WorkflowEdge counterpart on either side (the link lives in
 *  SinkBranch.linked_workflow_id) so dirty / save filters must drop
 *  them before serialisation. */
export const SUBFLOW_LINK_EDGE_PREFIX = "sf-link:";

/** True when the edge is a sub-flow link virtual edge. */
export function isSubflowLinkEdge<T extends { id: string }>(e: T): boolean {
  return e.id.startsWith(SUBFLOW_LINK_EDGE_PREFIX);
}


// ---------------------------------------------------------------------------
// Save-time partitioning + dirty detection (ADR-0008 PR3b3c)
//
// These pure helpers were extracted from WorkflowEditor's saveMutation /
// dirty memos so the serialisation-boundary logic has ONE tested source
// of truth. Getting any of these wrong silently corrupts a save (a
// sub-flow node leaking into the parent definition, or a dirty sub-flow
// never persisted). Typed against minimal structural shapes so they
// stay framework-free.
// ---------------------------------------------------------------------------

/** Structural shape of a canvas node for the partition/dirty helpers.
 *  The editor's ReactFlow ``Node<NodeData>`` satisfies this. */
export interface CanvasNodeLike {
  id: string;
  /** ReactFlow node type — ``"subflowRegion"`` for region wrappers. */
  type?: string;
  /** Set on sub-flow child nodes (the region group id). */
  parentNode?: string | undefined;
}

/** Structural shape of a canvas edge. ReactFlow's ``Edge`` satisfies it. */
export interface CanvasEdgeLike {
  id: string;
  source: string;
  target: string;
}


/** Split the unified canvas into the MAIN workflow's nodes/edges,
 *  dropping everything that belongs to a sub-flow region: the region
 *  wrapper (``type === "subflowRegion"``), any parented child, any
 *  prefixed child node/edge, and the virtual ``sf-link:`` edges.
 *
 *  Single source of truth for the filter that previously lived
 *  (duplicated) in buildLiveDefinition, saveMutation, createBranchSubflow
 *  and deleteSubflow. A regression here would leak sub-flow nodes into
 *  the parent definition on save.
 */
export function splitMainFromSubflowCanvas<
  N extends CanvasNodeLike,
  E extends CanvasEdgeLike,
>(nodes: N[], edges: E[]): { mainNodes: N[]; mainEdges: E[] } {
  const mainNodes = nodes.filter(
    (n) =>
      n.type !== "subflowRegion" &&
      n.parentNode === undefined &&
      !isSubflowChildNode(n),
  );
  const mainEdges = edges.filter(
    (e) =>
      !isSubflowChildNode({ id: e.source }) &&
      !isSubflowChildNode({ id: e.target }) &&
      !isSubflowChildNode(e) &&
      !isSubflowLinkEdge(e),
  );
  return { mainNodes, mainEdges };
}


/** Given the per-sub-flow snapshots captured at hydration and the live
 *  canvas, return the set of sub-flow workflow ids whose definition has
 *  diverged (and therefore must be re-saved). Compares the JSON of the
 *  extracted slice against the snapshot — same shape on both sides.
 */
export function computeDirtySubflowIds(
  snapshots: Record<string, string>,
  canvasNodes: CanvasFlowNodeLike[],
  canvasEdges: CanvasFlowEdgeLike[],
): string[] {
  const dirty: string[] = [];
  for (const sfId of Object.keys(snapshots)) {
    const slice = extractSubflowSliceFromCanvas(sfId, canvasNodes, canvasEdges);
    const live = JSON.stringify({ n: slice.nodes, e: slice.edges });
    if (live !== snapshots[sfId]) dirty.push(sfId);
  }
  return dirty;
}


/** Whether the MAIN workflow needs a createVersion on save. We skip it
 *  when ONLY sub-flows changed — otherwise every sub-flow edit would
 *  bump a useless no-op version on the parent. Save the main when it's
 *  actually modified, or when nothing else is dirty (a plain main save).
 */
export function shouldSaveMain(
  isMainModified: boolean,
  dirtySubflowCount: number,
): boolean {
  return isMainModified || dirtySubflowCount === 0;
}
